import React from 'react';
import { Box, Divider, Typography } from '@mui/material';

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  block: { bgcolor: 'background.paper', borderRadius: 3, px: 2, pt: 1, pb: 0.5, overflow: 'hidden' },
  title: {
    textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.65rem',
    fontWeight: 700, display: 'block', pt: 1, pb: 0.5,
  },
  chip: {
    minWidth: 28, height: 28, borderRadius: '50%', px: 0.5,
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.78rem', fontWeight: 700,
  },
};

// ── DetailRow ─────────────────────────────────────────────────────────────────

export function DetailRow({ icon, label, value, chip }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', py: 1.25, gap: 1.5, minWidth: 0 }}>
      <Box component="span" className="material-icons" sx={{ fontSize: 18, color: 'text.disabled', width: 20, flexShrink: 0 }}>
        {icon}
      </Box>
      <Typography variant="body2" color="text.secondary"
        sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
      {chip !== undefined ? (
        <Box sx={{ ...styles.chip, bgcolor: chip === 0 || chip === '0' ? 'info.main' : 'success.main' }}>
          {chip}
        </Box>
      ) : (
        <Typography variant="body2" fontWeight={600} color="text.primary"
          sx={{ maxWidth: '55%', wordBreak: 'break-word', textAlign: 'right' }}>
          {value ?? '-'}
        </Typography>
      )}
    </Box>
  );
}

// ── DetailBlock ───────────────────────────────────────────────────────────────
//
// Props:
//   title   — string, shown as section header
//   rows    — [{ icon, label, value, chip? }]
//   sections — [{ title, rows }] — for blocks with multiple sub-sections
//              (mutually exclusive with title + rows)
//   groupDivider — 'dashed' | 'solid' (default 'dashed') — divider between
//                  repeated groups when rows repeat across entries

export default function DetailBlock({ title, rows, sections }) {
  const content = sections ?? [{ title, rows }];

  return (
    <Box sx={styles.block}>
      {content.map((section, si) => (
        <React.Fragment key={si}>
          {si > 0 && <Divider />}
          {section.title && (
            <Typography variant="caption" color="text.disabled" sx={styles.title}>
              {section.title}
            </Typography>
          )}
          {(section.rows ?? []).map((row, ri) => (
            <React.Fragment key={ri}>
              {ri > 0 && (
                row.divider
                  ? <Divider sx={{ borderStyle: row.divider }} />
                  : <Divider />
              )}
              {!row.divider && (
                <DetailRow icon={row.icon} label={row.label} value={row.value} chip={row.chip} />
              )}
            </React.Fragment>
          ))}
        </React.Fragment>
      ))}
    </Box>
  );
}
